import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE from '../utils/api';

const THEME_PRESETS = {
  indigo: { sidebarColor: '#3730a3', primaryColor: '#4f46e5', accentColor: '#6366f1', loginBgFrom: '#312e81', loginBgTo: '#1e40af' },
  blue: { sidebarColor: '#1e3a5f', primaryColor: '#2563eb', accentColor: '#3b82f6', loginBgFrom: '#1e3a5f', loginBgTo: '#1d4ed8' },
  green: { sidebarColor: '#14532d', primaryColor: '#16a34a', accentColor: '#22c55e', loginBgFrom: '#14532d', loginBgTo: '#15803d' },
  red: { sidebarColor: '#7f1d1d', primaryColor: '#dc2626', accentColor: '#ef4444', loginBgFrom: '#7f1d1d', loginBgTo: '#991b1b' },
  purple: { sidebarColor: '#581c87', primaryColor: '#9333ea', accentColor: '#a855f7', loginBgFrom: '#581c87', loginBgTo: '#6b21a8' },
  teal: { sidebarColor: '#134e4a', primaryColor: '#0d9488', accentColor: '#14b8a6', loginBgFrom: '#134e4a', loginBgTo: '#115e59' },
  orange: { sidebarColor: '#7c2d12', primaryColor: '#ea580c', accentColor: '#f97316', loginBgFrom: '#7c2d12', loginBgTo: '#9a3412' },
  slate: { sidebarColor: '#1e293b', primaryColor: '#475569', accentColor: '#64748b', loginBgFrom: '#0f172a', loginBgTo: '#1e293b' },
  rose: { sidebarColor: '#881337', primaryColor: '#e11d48', accentColor: '#f43f5e', loginBgFrom: '#881337', loginBgTo: '#9f1239' },
  cyan: { sidebarColor: '#164e63', primaryColor: '#0891b2', accentColor: '#06b6d4', loginBgFrom: '#164e63', loginBgTo: '#155e75' },
};

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    themePreset: 'indigo',
    sidebarColor: '#3730a3',
    primaryColor: '#4f46e5',
    accentColor: '#6366f1',
    loginBgFrom: '#312e81',
    loginBgTo: '#1e40af',
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/settings/public`)
      .then(res => {
        const t = {
          themePreset: res.data.themePreset || 'indigo',
          sidebarColor: res.data.sidebarColor || '#3730a3',
          primaryColor: res.data.primaryColor || '#4f46e5',
          accentColor: res.data.accentColor || '#6366f1',
          loginBgFrom: res.data.loginBgFrom || '#312e81',
          loginBgTo: res.data.loginBgTo || '#1e40af',
        };
        setTheme(t);
        applyCSS(t);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const applyCSS = (t) => {
    const root = document.documentElement;
    root.style.setProperty('--sidebar-color', t.sidebarColor);
    root.style.setProperty('--primary-color', t.primaryColor);
    root.style.setProperty('--accent-color', t.accentColor);
    root.style.setProperty('--login-bg-from', t.loginBgFrom);
    root.style.setProperty('--login-bg-to', t.loginBgTo);
    // Compute lighter versions for hover/ring
    root.style.setProperty('--primary-hover', adjustBrightness(t.primaryColor, -15));
    root.style.setProperty('--primary-light', adjustBrightness(t.primaryColor, 40));
    root.style.setProperty('--sidebar-hover', adjustBrightness(t.sidebarColor, -20));
    root.style.setProperty('--sidebar-active', adjustBrightness(t.sidebarColor, -30));
  };

  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    applyCSS(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, THEME_PRESETS, loaded }}>
      {children}
    </ThemeContext.Provider>
  );
};

function adjustBrightness(hex, amount) {
  hex = hex.replace('#', '');
  const num = parseInt(hex, 16);
  let r = Math.min(255, Math.max(0, ((num >> 16) & 0xff) + amount));
  let g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
  let b = Math.min(255, Math.max(0, (num & 0xff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

export default ThemeContext;
